/**
 * Meat-on-Bone Badge Renderer
 *
 * Generates HTML for the "meat on the bone" profit margin display
 */

import type { Lot, PortalSettings } from '../../types';
import { parseCurrencyValue, getMeatBadgeColor, escapeAttr } from '../formatting';
import { calculateMeatOnBone } from '../lotOperations';
import { formatMeatValue } from './helpers';
import styles from './tableCells.module.css';

export function buildMeatBadge(lot: Lot, settings: PortalSettings | null | undefined): string {
  const l = lot;
  let meatOnBoneDisplay = '—';
  const meatCalc = calculateMeatOnBone(l, settings);

  // Check if user has bid on this lot (compute once, used for all display variants)
  const hasBid = !!l.bidding;
  const bidAmount = typeof l.bidAmount === 'number' ? l.bidAmount : null;
  const currentBidAmount = parseCurrencyValue(
    l.currentBid != null ? l.currentBid : l.currentBidRaw
  );

  // User is winning if they have a bid and it's >= current bid
  const isWinning =
    hasBid && bidAmount != null && currentBidAmount != null && bidAmount >= currentBidAmount;

  const winningEmoji = isWinning
    ? '<span class="' + styles.meatBadgeTrophy + '" aria-label="Winning">🏆</span>'
    : '';

  if (meatCalc) {
    const { meatLow, meatHigh } = meatCalc;
    const meatLowDisplay = formatMeatValue(meatLow);
    const meatHighDisplay = formatMeatValue(meatHigh);

    if (meatLowDisplay !== '—' || meatHighDisplay !== '—') {
      const badgeColor = getMeatBadgeColor(meatLow, meatHigh);
      const badgeColorClass =
        badgeColor === 'green'
          ? styles.meatBadgeGreen
          : badgeColor === 'yellow'
            ? styles.meatBadgeYellow
            : styles.meatBadgeRed;

      const classNames = [styles.meatBadge, badgeColorClass];

      if (hasBid) {
        classNames.push(styles.meatBadgeHasBid);
      }

      meatOnBoneDisplay =
        '<div class="' +
        classNames.join(' ') +
        '" data-action="edit-estimate" data-id="' +
        escapeAttr(l.id) +
        '" role="button" tabindex="0" title="Edit price estimates" aria-label="Edit price estimates">' +
        winningEmoji +
        '<div class="' +
        styles.meatBadgeValue +
        '">' +
        meatLowDisplay +
        '</div><div class="' +
        styles.meatBadgeSeparator +
        '">⋯</div><div class="' +
        styles.meatBadgeValue +
        '">' +
        meatHighDisplay +
        '</div></div>';
    } else {
      // Show Estimate button when no meat badge can be calculated
      const buttonClass = hasBid
        ? styles.estimateButton + ' ' + styles.estimateButtonHasBid
        : styles.estimateButton;
      meatOnBoneDisplay =
        '<div class="' +
        buttonClass +
        '" data-action="edit-estimate" data-id="' +
        escapeAttr(l.id) +
        '" role="button" tabindex="0" title="Add price estimates" aria-label="Add price estimates">' +
        winningEmoji +
        'Estimate</div>';
    }
  } else {
    // Show Estimate button when no next bid is available
    const buttonClass = hasBid
      ? styles.estimateButton + ' ' + styles.estimateButtonHasBid
      : styles.estimateButton;
    meatOnBoneDisplay =
      '<div class="' +
      buttonClass +
      '" data-action="edit-estimate" data-id="' +
      escapeAttr(l.id) +
      '" role="button" tabindex="0" title="Add price estimates" aria-label="Add price estimates">' +
      winningEmoji +
      'Estimate</div>';
  }

  return meatOnBoneDisplay;
}
